import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Text, BlockStack, Spinner, Divider, Box, InlineStack } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { fetchAuditLogs } from "../../services/audit-logs.service";
import { AuditLogEntry } from "./audit-log-entry";
import type { AuditLogRow } from "./auditLogTypes";
import PaginationControls from "../pagination/PaginationControls";

type Props = {
  companyId?: string;
  subjectType?: string;
  subjectId?: string;
  maxHeight?: string;
  pageSize?: number;
  showDetails?: boolean;
};

/**
 * A reusable list of audit logs with internal fetching and pagination.
 */
export function AuditLogList({ 
  companyId: companyIdProps, 
  subjectType, 
  subjectId, 
  maxHeight = "450px",
  pageSize: initialPageSize = 10,
  showDetails = false
}: Props) {
  const { companyId: companyIdParams } = useParams();
  const companyId = companyIdProps || companyIdParams || "";
  const { t } = useTranslation();
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: initialPageSize,
  });

  const { page, pageSize } = pagination;
  const limit = pageSize;
  const skip = (page - 1) * pageSize;

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    fetchAuditLogs(companyId, { subjectType, subjectId, limit, skip })
      .then((data) => {
        setRows((data.rows || []) as AuditLogRow[]);
        setTotal(data.count || 0);
      })
      .catch((e: any) => {
        setError(e?.response?.status === 403 ? "forbidden" : "error");
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [companyId, subjectType, subjectId, limit, skip]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePageChange = (nextPage: number) => {
    setPagination(p => ({ ...p, page: nextPage }));
  };

  const handlePageSizeChange = (nextSize: number) => {
    setPagination({ page: 1, pageSize: nextSize });
  };

  if (error === "forbidden") {
    return (
      <Box padding="400">
        <Text as="p" tone="critical">{t("access_denied", { defaultValue: "Access denied" })}</Text>
      </Box>
    );
  }

  if (error === "error") {
    return (
      <Box padding="400">
        <Text as="p" tone="critical">
          {t("error_loading_data", { defaultValue: "Failed to load activity records" })}
        </Text>
      </Box>
    );
  }

  const totalPages = Math.ceil(total / pageSize);
  const hasNextPage = page < totalPages;
  const hasPreviousPage = page > 1;

  return (
    <BlockStack gap="300">
      <Box 
        padding="0" 
        minHeight="100px"
      >
        {loading ? (
          <Box padding="400">
            <InlineStack align="center">
              <Spinner size="small" />
            </InlineStack>
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="400">
            <Text as="p" tone="subdued">
              {t("no_data", { defaultValue: "No activity records found" })}
            </Text>
          </Box>
        ) : (
          <BlockStack gap="0">
            {rows.map((row, i) => (
              <div key={row.id}>
                {i > 0 && <Divider />}
                <AuditLogEntry
                  companyId={companyId}
                  row={row}
                  showSubjectLine={true} 
                  showDetails={showDetails}
                  padded
                />
              </div>
            ))}
          </BlockStack>
        )}
      </Box>

      {!loading && total > 0 && (
        <Box padding="200">
          <PaginationControls
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20]}
            isLoading={loading}
          />
        </Box>
      )}
    </BlockStack>
  );
}
